import { createDataKey } from '../../../shared/utils'
import { BudgetComment, StatementColumn, StatementDataType } from '../types'
import { FinancialRowType, type EnhancedStatementRow } from '../../financialStatements/utils/dataEnhancement'

/**
 * Comments map: indexed by cell key for fast lookups
 */
export type CommentsMap = Map<string, BudgetComment[]>

/**
 * Check if a row can have comments
 * Only ACCOUNT, DIMENSION, and HEADING rows are commentable
 * INVESTMENT, AMENDMENT, MENU rows inherit _sourceAccountCode from parent but should not be commentable
 */
export const isRowCommentable = (row: EnhancedStatementRow | null | undefined): boolean => {
  if (!row) return false

  return (
    row.rowType === FinancialRowType.ACCOUNT ||
    row.rowType === FinancialRowType.DIMENSION ||
    row.rowType === FinancialRowType.HEADING
  )
}

/**
 * Build dimension query string for a single dimension
 * Returns query in format: [{"o":"%","v":"dimensionId"}]
 */
export const buildDimensionQuery = (dimensionId: string): string => {
  return JSON.stringify([{ o: '%', v: dimensionId }])
}

interface CellKeyParams {
  accountCode?: string | number
  headingId?: string | number
  dimensionId?: string | null
  fiscalYearGroupId?: string | number
  year?: number
  month?: number
}

/**
 * Single source of truth for building comment cell keys.
 * Both getCellKey (from row+column) and getCellKeyFromComment (from comment data) use this.
 */
const buildCellKey = (params: CellKeyParams): string | undefined => {
  const { accountCode, headingId, dimensionId = null, fiscalYearGroupId, year, month } = params

  // Determine row type and id
  let type: string
  let id: string | number
  if (headingId && !accountCode) {
    type = 'heading'
    id = headingId
  } else if (accountCode) {
    type = 'account'
    id = accountCode
  } else {
    return undefined
  }

  const base = `${type}-${id}`

  // Fiscal year level
  if (fiscalYearGroupId && !month && !year) {
    return `${base}-${dimensionId}-groups-${fiscalYearGroupId}`
  }

  // Period level
  if (year && month) {
    return `${base}-${dimensionId}-elements-${createDataKey(year, month)}`
  }

  return base
}

/**
 * Generate cell key for comment mapping from row + column
 * Returns undefined for actuals columns or invalid rows
 */
export const getCellKey = (
  row: EnhancedStatementRow | null | undefined,
  column: StatementColumn | null | undefined
): string | undefined => {
  if (!row || !column || column.dataType === StatementDataType.actuals) return undefined
  if (!isRowCommentable(row)) return undefined

  return buildCellKey({
    accountCode: row._sourceAccountCode,
    headingId: row._sourceStatementRowId,
    dimensionId: row.dimensionId || null,
    fiscalYearGroupId: column.periodGroup && 'id' in column.periodGroup ? column.periodGroup.id : undefined,
    year: column.year,
    month: column.month
  })
}

/**
 * Generate cell key from comment data
 */
export const getCellKeyFromComment = (comment: BudgetComment): string => {
  return (
    buildCellKey({
      accountCode: comment.accountCode,
      headingId: comment.incomeStatementRowId,
      dimensionId: comment.dimensionId || null,
      fiscalYearGroupId: comment.fiscalYearId,
      year: comment.year,
      month: comment.month
    }) ?? ''
  )
}
